import React from 'react';

import { RadioGroup, Flex } from '@lucidhq/lucidium';

import { TemplateQuestion } from '~/components/Shared/hooks/useFetchTemplateQuestions';
import { EnhancedTemplateQuestion, NOTA } from '~/utils/templateQuestions';
import { resetCustomAnswers } from '~/utils/templateQuestions';

import { useCampaignManager } from '../../CampaignManagerProvider';
import { SurveyStepIds } from '../../useCampaignManagerSteps';
import { useBehavioralQuestions } from '../BehavioralQuestionsStep/BehavioralQuestionsProvider';
import { useStandardQuestionsProvider } from '../StandardQuestionsStep/StandardQuestionsProvider';

const options = [
  { label: 'Default Brand Answers', value: true },
  { label: 'Custom', value: false }
];

type AnswersRadioGroupProps = {
  value?: boolean;
  onSave: (data: Partial<TemplateQuestion>) => void;
  question: EnhancedTemplateQuestion;
};

// NOTE: this method is used in order to ensure that customAnswers and Answers are in sync.
const findCustomizedNOTAValue = (
  answers: string[],
  question: EnhancedTemplateQuestion,
  notaAnswer: string
) => {
  const hasNOTAValue =
    !!question?.none_of_the_above_answer &&
    answers.includes(question.none_of_the_above_answer);

  if (hasNOTAValue) {
    const filteredAnswers = answers.filter(
      answer =>
        answer !== question.none_of_the_above_answer || answer !== notaAnswer
    );

    return [...filteredAnswers, notaAnswer];
  }

  // NOTE: the reason for both NOTA conditions here is to ensure that a customized NOTA answer is not duplicated in the answers array.
  if (
    !!question.none_of_the_above_answer &&
    !answers.includes(notaAnswer) &&
    !answers.includes(question?.none_of_the_above_answer)
  ) {
    return [...answers, notaAnswer];
  }

  return answers;
};

// NOTE: both of the following functions resemble each other, but are not the same as the functions in QuestionLogicItem.tsx
// In order to avoid (more) confusion these methods have been separated out by file.
export const getUpdatedAnswers = (
  question: EnhancedTemplateQuestion,
  answers: string[],
  translatedNOTAVal: string
) => {
  const notaAnswer = translatedNOTAVal ?? NOTA;

  // NOTE: The following adjustment has been made to allow custom NOTA values
  // this method now is isolated to any events initiated from this component.
  if (!!question.none_of_the_above_answer) {
    return findCustomizedNOTAValue(answers, question, notaAnswer);
  }

  if (
    question.none_of_the_above_answer === null &&
    answers.includes(notaAnswer)
  ) {
    return answers.filter(answer => answer !== notaAnswer);
  }

  return answers;
};

const getAnswersRecord = (
  val: boolean,
  question: EnhancedTemplateQuestion,
  translatedNOTAVal: string
) => {
  // edge case for empty answers array API error
  if (question.question_type.includes('table')) return {};

  if (val) {
    const updatedBrandAnswers = getUpdatedAnswers(
      question,
      question.defaultBrandAnswers as string[],
      translatedNOTAVal
    );
    // NOTE: This condition is necessary so that the API does not overwrite the NOTA value in the Answers array with the customized one.
    const hasCustomNOTAVal =
      !!question?.none_of_the_above_answer &&
      question.none_of_the_above_answer !== translatedNOTAVal;

    return {
      answers: updatedBrandAnswers,
      defaultBrandAnswers: updatedBrandAnswers,
      ...(hasCustomNOTAVal && {
        none_of_the_above_answer: translatedNOTAVal
      })
    };
  }

  const updatedCustomAnswers = resetCustomAnswers(question);

  return {
    answers: updatedCustomAnswers,
    customAnswers: updatedCustomAnswers
  };
};

export const AnswersRadioGroup = ({
  value,
  onSave,
  question
}: AnswersRadioGroupProps) => {
  const { options: stepOptions } = useCampaignManager();
  const { translatedNOTAValue = '' } = useStandardQuestionsProvider();
  const { referenceNOTAValue = '' } = useBehavioralQuestions();

  const isStandard =
    stepOptions?.activeChildStep === SurveyStepIds.STANDARD_QUESTIONS;

  const currentNOTAValue = isStandard
    ? translatedNOTAValue
    : referenceNOTAValue;

  return (
    <Flex lss={{ my: 'sm' }}>
      <RadioGroup
        name="use_answer_meta"
        isInline
        options={options}
        value={value}
        onChange={val => {
          const isCustomGridKPI = !!question.question_type.includes('table');

          const answerRecord = getAnswersRecord(
            val,
            question,
            currentNOTAValue
          );

          onSave({
            use_answer_meta: val,
            ...answerRecord,
            ...(isCustomGridKPI && {
              rows: !!val ? question?.defaultBrandRows : question?.customRows
            })
          });
        }}
      />
    </Flex>
  );
};
